import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./CascadePaywall.module.scss"

/**
 * CascadePaywall (`/cascade`) — a self-contained NON-ONBOARDING upsell for Private
 * Photo Vault built as a TWO-VESSEL POUR. Your camera roll is staged as an open,
 * tilting tumbler anyone can look into; the vault is a tall corked jar. You grab
 * the tumbler and TIP it — your memories cascade out of the lip and fill the vault
 * jar from the bottom up behind a glassy blue meniscus, settling into a rising
 * mosaic. A live "moved in · N" counter climbs; when the tumbler empties the cork
 * seats and a "Private · Backed up · On every device" plate settles.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/cascade.md
 *
 * Mechanic (new to the ledger): TILT-TO-POUR / DECANT. An analog, directional tip
 * of a vessel — gravity does the pouring over time while it's tilted. Not a scrub,
 * hold, flip, toss, dial, lever, stitch, lasso, place-a-piece, rope-haul, key-wind,
 * raise-resin, wipe, stamp or boot; and pointedly NOT a grey→colour reveal — the
 * photos stay full colour throughout, only WHERE they live changes (exposed glass →
 * sealed vault). Auto-demo tips a little on load then hands the vessel over; a
 * "Pour it all in" button and Enter/Space also complete it.
 *
 * Value framing (honest, behavioural): CONTRAST + LOSS-AVERSION + CONSOLIDATION —
 * get every memory out of the open camera roll (visible to anyone with your phone,
 * gone with a lost phone) into one private, backed-up home. Register is effortless
 * relief / satisfying flow, lightly playful — distinct from the fear / luxury /
 * reverence / mechanical / precious registers of prior concepts.
 *
 * Honest & App Store compliant: the features are real Premium features (automatic
 * backup, full-quality originals, private & encrypted, on every device). Copy never
 * implies photos are lost if the plan ends — it says the opposite ("nothing is ever
 * deleted — your photos are always yours"). Yearly is the featured, default plan and
 * carries the most prominent price; monthly is small and secondary. Save % is exact
 * yearly-vs-12×-monthly arithmetic; no countdown or fake scarcity. Photos are bundled
 * demo images already in the repo (no network egress).
 */

type PlanKey = "yearly" | "monthly"

// your most recent memories — bundled everyday photos, no network egress
const MEMORIES = [
  { src: "/recall/sunset-1.jpg", pos: "50% 45%" },
  { src: "/recall/dog-2.jpg", pos: "50% 40%" },
  { src: "/recall/beach-2.jpg", pos: "50% 55%" },
  { src: "/recall/coffee-1.jpg", pos: "50% 50%" },
  { src: "/recall/mountain-1.jpg", pos: "50% 50%" },
  { src: "/recall/flower-2.jpg", pos: "50% 45%" },
  { src: "/recall/city-2.jpg", pos: "50% 50%" },
  { src: "/recall/food-1.jpg", pos: "50% 50%" },
  { src: "/recall/beach-3.jpg", pos: "50% 55%" },
]
const TOTAL = MEMORIES.length

const POUR_SPEED = 3.1 // chips per second at full tilt
const TILT_ON = 0.32 // tilt past which the pour flows

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// real Premium features — kept as a compact, secondary line (no hero perk grid)
const FEATURES = [
  { icon: "cloud", label: "Backed up automatically" },
  { icon: "hd", label: "Full-quality originals" },
  { icon: "lock", label: "Private & encrypted" },
  { icon: "devices", label: "On every device" },
]

const Icon = ({ name, size = 15 }: { name: string; size?: number }) => {
  const c = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      {name === "lock" && (
        <>
          <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" {...c} />
          <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" {...c} />
        </>
      )}
      {name === "hd" && (
        <>
          <rect x="3.5" y="5" width="17" height="14" rx="2.4" {...c} />
          <path d="M7 9.5v5M7 12h2.4M9.4 9.5v5" {...c} />
          <path
            d="M12.8 9.5v5h1.8a2 2 0 0 0 2-2v-1a2 2 0 0 0-2-2h-1.8Z"
            {...c}
          />
        </>
      )}
      {name === "cloud" && (
        <path
          d="M7 18a4 4 0 0 1-.4-8 5.5 5.5 0 0 1 10.6 1.2A3.4 3.4 0 0 1 17 18H7Z"
          {...c}
        />
      )}
      {name === "devices" && (
        <>
          <rect x="3" y="6" width="12" height="9" rx="1.6" {...c} />
          <rect x="16" y="9" width="5" height="9" rx="1.4" {...c} />
          <path d="M6 18h6" {...c} />
        </>
      )}
      {name === "eye" && (
        <>
          <path
            d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
            {...c}
          />
          <circle cx="12" cy="12" r="2.6" {...c} />
        </>
      )}
      {name === "shield" && (
        <path
          d="M12 2.6l7 2.7v5.2c0 4.6-3 8.2-7 10.9-4-2.7-7-6.3-7-10.9V5.3l7-2.7Z"
          {...c}
        />
      )}
    </svg>
  )
}

interface Flyer {
  id: number
  src: string
  pos: string
  dx: number
  dy: number
  rot: number
}

const CascadePaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const yearlyTrial = yearlyProduct?.hasTrial
    ? (yearlyProduct?.introPricing?.period ?? 7)
    : 0
  const monthlyTrial = monthlyProduct?.hasTrial
    ? (monthlyProduct?.introPricing?.period ?? 3)
    : 0

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.max(0, Math.round((1 - y / (m * 12)) * 100))
  }, [yearlyProduct, monthlyProduct])

  // ── pour state ──────────────────────────────────────────────────────────────
  const [tilt, setTilt] = useState(0) // visible tumbler tilt 0..1
  const [settled, setSettled] = useState(0) // chips landed in the vault
  const [poured, setPoured] = useState(0) // chips that have left the tumbler
  const [flyers, setFlyers] = useState<Flyer[]>([])
  const [engaged, setEngaged] = useState(false) // has the user tipped it yet
  const [sealed, setSealed] = useState(false)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)

  const stageRef = useRef<HTMLDivElement | null>(null)
  const lipRef = useRef<HTMLDivElement | null>(null)
  const mouthRef = useRef<HTMLDivElement | null>(null)

  const targetTilt = useRef(0)
  const tiltRef = useRef(0)
  const progressRef = useRef(0) // 0..TOTAL float
  const flyerId = useRef(0)
  const autoPour = useRef(false)
  const rafRef = useRef<number | null>(null)
  const lastT = useRef<number | null>(null)
  const dragBase = useRef<{ y: number; tilt: number } | null>(null)

  // spawn a cascading memory from the tumbler lip to the vault mouth
  const spawn = useCallback((index: number) => {
    const stage = stageRef.current
    const lip = lipRef.current
    const mouth = mouthRef.current
    if (!stage || !lip || !mouth) return
    const s = stage.getBoundingClientRect()
    const l = lip.getBoundingClientRect()
    const m = mouth.getBoundingClientRect()
    const lx = l.left + l.width / 2 - s.left
    const ly = l.top + l.height / 2 - s.top
    const mx = m.left + m.width / 2 - s.left
    const my = m.top + m.height / 2 - s.top
    const mem = MEMORIES[index % TOTAL]
    const id = flyerId.current++
    setFlyers((f) => [
      ...f,
      {
        id,
        src: mem.src,
        pos: mem.pos,
        dx: mx - lx,
        dy: my - ly,
        rot: (index % 2 === 0 ? 1 : -1) * (18 + (index % 3) * 10),
      },
    ])
    // land the memory into the vault after the cascade animation
    window.setTimeout(() => {
      setFlyers((f) => f.filter((x) => x.id !== id))
      setSettled((n) => Math.min(TOTAL, n + 1))
    }, 640)
  }, [])

  // master loop: ease the tilt, and pour while tipped
  useEffect(() => {
    if (reduceMotion) return
    const tick = (now: number) => {
      const dt = lastT.current == null ? 0 : (now - lastT.current) / 1000
      lastT.current = now

      const tt = autoPour.current ? 1 : targetTilt.current
      // ease visible tilt toward target
      const nt = tiltRef.current + (tt - tiltRef.current) * Math.min(1, dt * 9)
      tiltRef.current = nt
      setTilt(nt)

      if (progressRef.current < TOTAL && nt > TILT_ON) {
        progressRef.current = Math.min(
          TOTAL,
          progressRef.current + dt * nt * POUR_SPEED,
        )
        const p = Math.floor(progressRef.current)
        setPoured((prev) => {
          if (p > prev) for (let i = prev; i < p; i++) spawn(i)
          return Math.max(prev, p)
        })
        if (progressRef.current >= TOTAL) {
          autoPour.current = false
          targetTilt.current = 0
        }
      }

      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      lastT.current = null
    }
  }, [reduceMotion, spawn])

  // seal when everything has landed
  useEffect(() => {
    if (settled >= TOTAL && !sealed) {
      setSealed(true)
      native.logMixpanelEvent("html_cascade_sealed", {})
    }
  }, [settled, sealed])

  // reduced motion — arrive already poured & sealed, no animation
  useEffect(() => {
    if (!reduceMotion) return
    progressRef.current = TOTAL
    setPoured(TOTAL)
    setSettled(TOTAL)
    setEngaged(true)
    setShowClose(true)
  }, [reduceMotion])

  // gentle teaching tip on load, then hand the vessel over
  useEffect(() => {
    if (reduceMotion) return
    const t1 = window.setTimeout(() => {
      targetTilt.current = 0.72
    }, 700)
    const t2 = window.setTimeout(() => {
      targetTilt.current = 0
    }, 1650)
    const t3 = window.setTimeout(() => setShowClose(true), 1400)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(t3)
    }
  }, [reduceMotion])

  // colour the safe-area strips to the warm table
  useEffect(() => {
    actions.setHeaderInsetColor("#f4eee6")
    actions.setFooterInsetColor("#ffffff")
  }, [actions])

  // ── input: tip the tumbler ──────────────────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent) => {
    if (sealed) return
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    dragBase.current = { y: e.clientY, tilt: tiltRef.current }
    if (!engaged) {
      setEngaged(true)
      native.logMixpanelEvent("html_cascade_tip", {})
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragBase.current || sealed) return
    const dy = dragBase.current.y - e.clientY // up = tip
    targetTilt.current = clamp(dragBase.current.tilt + dy / 150, 0, 1)
  }
  const onPointerUp = () => {
    dragBase.current = null
    if (!autoPour.current) targetTilt.current = 0
  }

  const pourAll = () => {
    if (sealed) return
    setEngaged(true)
    autoPour.current = true
    native.logMixpanelEvent("html_cascade_pour_all", {})
  }

  const onStageKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      pourAll()
    }
  }

  // ── plan + purchase wiring ──────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_cascade_purchase", { plan })
    if (product) native.subscribe(product)
  }

  const trialDays = plan === "yearly" ? yearlyTrial : monthlyTrial
  const ctaSub =
    plan === "yearly"
      ? trialDays > 0
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : trialDays > 0
        ? `${trialDays} days free, then ${monthlyPrice}/mo · cancel anytime`
        : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  // derived visuals
  const fillPct = (settled / TOTAL) * 100
  const remaining = MEMORIES.slice(poured) // still in the tumbler
  const pouring = tilt > TILT_ON && poured < TOTAL
  const vaultSrcs = MEMORIES.slice(0, settled)

  const hint = sealed
    ? "Everything's in your vault"
    : engaged
      ? poured > 0
        ? "Keep tipping — pour it all in"
        : "Tip it toward the vault"
      : "Grab the glass and tip it →"

  return (
    <div className={styles.page}>
      <button
        className={`${styles.close} ${showClose ? styles.closeShown : ""}`}
        onClick={handleClose}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <span className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <Icon name="shield" size={13} />
          </span>
          Private Photo Vault
        </span>
        <h1 className={styles.title}>
          Pour your camera roll
          <br />
          into one private place.
        </h1>
        <p className={styles.sub}>
          Your camera roll is open to anyone holding your phone. Tip it into
          your vault — private, full-quality, backed up everywhere.
        </p>
      </header>

      {/* ── the pour tableau ─────────────────────────────────────────────────── */}
      <section
        className={styles.stage}
        ref={stageRef}
        role="button"
        tabIndex={0}
        aria-label="Tip your camera roll into your vault"
        onKeyDown={onStageKey}
      >
        {/* camera roll — the open, tipping tumbler */}
        <div
          className={`${styles.tumblerWrap} ${sealed ? styles.tumblerDone : ""}`}
          style={{ ["--tilt" as any]: tilt }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className={styles.vesselTag}>
            <Icon name="eye" size={12} />
            Camera roll · open
          </span>
          <div className={styles.tumbler}>
            <span className={styles.tumblerGlass} aria-hidden />
            <div className={styles.heap}>
              {remaining.map((m, i) => (
                <span
                  key={m.src}
                  className={styles.heapChip}
                  style={{
                    backgroundImage: `url(${m.src})`,
                    backgroundPosition: m.pos,
                    ["--i" as any]: i,
                    zIndex: remaining.length - i,
                  }}
                />
              ))}
              {remaining.length === 0 && (
                <span className={styles.heapEmpty}>empty</span>
              )}
            </div>
            <span className={styles.lip} ref={lipRef} aria-hidden />
          </div>
          <span className={styles.handle} aria-hidden>
            <span className={styles.handleDot} />
            <span className={styles.handleDot} />
          </span>
        </div>

        {/* flying memories mid-cascade */}
        <div className={styles.flightLayer} aria-hidden>
          {flyers.map((f) => (
            <span
              key={f.id}
              className={styles.flyer}
              style={{
                backgroundImage: `url(${f.src})`,
                backgroundPosition: f.pos,
                left: 0,
                top: 0,
                transform: "translate(-50%,-50%)",
                ["--dx" as any]: `${f.dx}px`,
                ["--dy" as any]: `${f.dy}px`,
                ["--r" as any]: `${f.rot}deg`,
                // anchor start at lip via CSS var offsets applied in keyframe
              }}
              ref={(el) => {
                if (!el) return
                const stage = stageRef.current
                const lip = lipRef.current
                if (!stage || !lip) return
                const s = stage.getBoundingClientRect()
                const l = lip.getBoundingClientRect()
                el.style.left = `${l.left + l.width / 2 - s.left}px`
                el.style.top = `${l.top + l.height / 2 - s.top}px`
              }}
            />
          ))}
        </div>

        {/* the vault jar */}
        <div className={`${styles.jarWrap} ${sealed ? styles.jarSealed : ""}`}>
          <span className={styles.mouth} ref={mouthRef} aria-hidden />
          <span
            className={`${styles.cork} ${sealed ? styles.corkSeated : ""}`}
            aria-hidden
          />
          <div className={styles.jar}>
            <div className={styles.jarInner}>
              <div className={styles.vaultGrid}>
                {vaultSrcs.map((m, i) => (
                  <span
                    key={m.src}
                    className={styles.vaultChip}
                    style={{
                      backgroundImage: `url(${m.src})`,
                      backgroundPosition: m.pos,
                      ["--vi" as any]: i,
                    }}
                  />
                ))}
              </div>
              <div
                className={styles.liquid}
                style={{ height: `${Math.max(fillPct, settled ? 12 : 0)}%` }}
              >
                <span className={styles.meniscus} aria-hidden />
              </div>
              {pouring && <span className={styles.stream} aria-hidden />}
            </div>
            <span className={styles.jarGlass} aria-hidden />
            <span className={styles.jarEtch} aria-hidden>
              <Icon name="lock" size={14} />
            </span>
          </div>
          <span className={styles.coaster} aria-hidden />
          <span
            className={`${styles.counter} ${sealed ? styles.counterDone : ""}`}
          >
            <b>{settled}</b>
            <i>{sealed ? "all in · sealed" : `moved in / ${TOTAL}`}</i>
          </span>
        </div>

        <div className={styles.stageFoot}>
          {sealed ? (
            <span className={styles.sealPlate}>
              <Icon name="shield" size={14} />
              Private · Backed up · On every device
            </span>
          ) : (
            <>
              <span className={styles.hint}>{hint}</span>
              <button className={styles.pourAll} onClick={pourAll}>
                Pour it all in
              </button>
            </>
          )}
        </div>
      </section>

      <p className={styles.autoNote}>
        You had to tip it. <b>Premium pours in every new memory for you</b> —
        the instant you take it.
      </p>

      <ul className={styles.features}>
        {FEATURES.map((f) => (
          <li key={f.label} className={styles.feature}>
            <Icon name={f.icon} size={15} />
            {f.label}
          </li>
        ))}
      </ul>

      {/* ── plans — yearly featured, monthly small & secondary ─────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.sel : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
          <span className={styles.planMain}>
            <span className={styles.radio} aria-hidden />
            <span className={styles.planNameWrap}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.planNote}>
                {yearlyTrial > 0
                  ? `${yearlyTrial}-day free trial included`
                  : "Best value — everything, all year"}
              </span>
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>{yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.selMonth : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>
          {plan === "yearly" ? "Move everything in" : "Start monthly"}
        </span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Nothing is ever deleted — your photos are always yours. Pouring in only
        keeps a private, backed-up copy safe in your vault.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default CascadePaywall
